import React, { useEffect } from 'react'
import { LayoutDashboard, Database, Settings, User, X } from 'lucide-react'

type SidebarActive = 'dashboard' | 'settings' | 'profile'

interface SidebarProps {
  active: SidebarActive
  onNavigate: (section: SidebarActive) => void
  /** When set, sidebar is a drawer on small screens; open/close controlled by parent. */
  mobileOpen?: boolean
  onMobileClose?: () => void
}

export function Sidebar({
  active,
  onNavigate,
  mobileOpen = false,
  onMobileClose,
}: Readonly<SidebarProps>) {
  const handleNav = (section: SidebarActive) => {
    onNavigate(section)
    onMobileClose?.()
  }

  useEffect(() => {
    if (!mobileOpen || !onMobileClose) return
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onMobileClose()
    }
    document.addEventListener('keydown', onEscape)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onEscape)
      document.body.style.overflow = ''
    }
  }, [mobileOpen, onMobileClose])

  return (
    <>
      {mobileOpen && (
        <button
          type='button'
          aria-label='Close menu'
          className='lg:hidden fixed inset-0 z-40 bg-black/50 transition-opacity'
          onClick={onMobileClose}
        />
      )}
      <aside
        className={`
          w-64 flex flex-col border-r border-border bg-gradient-to-b from-slate-50/95 to-white shadow-xl shadow-slate-200/50 shrink-0
          fixed lg:relative inset-y-0 left-0 z-50 lg:z-auto
          transform transition-transform duration-200 ease-out
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        aria-hidden={!mobileOpen}
      >
        <div className='flex items-center justify-between p-4 lg:p-5'>
          <div className='flex items-center gap-3 px-3 py-3 rounded-xl bg-white/80 border border-slate-200/80 shadow-sm'>
            <div className='h-11 w-11 rounded-xl bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/30'>
              <Database className='text-white h-6 w-6' />
            </div>
            <span className='font-bold text-lg tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600'>
              DataMatch
            </span>
          </div>
          <button
            type='button'
            onClick={onMobileClose}
            className='lg:hidden p-2 rounded-lg hover:bg-slate-100 text-muted-foreground hover:text-foreground'
            aria-label='Close menu'
          >
            <X size={20} />
          </button>
        </div>

        <nav className='flex-1 px-3 space-y-1 overflow-auto'>
          <NavItem
            icon={<LayoutDashboard size={20} />}
            label='Dashboard'
            active={active === 'dashboard'}
            onClick={() => handleNav('dashboard')}
          />
          <NavItem
            icon={<Settings size={20} />}
            label='Competitors List'
            active={active === 'settings'}
            onClick={() => handleNav('settings')}
          />
        </nav>

        <div className='p-3 border-t border-border'>
          <NavItem
            icon={<User size={20} />}
            label='Profile'
            active={active === 'profile'}
            onClick={() => handleNav('profile')}
          />
        </div>
      </aside>
    </>
  )
}

interface NavItemProps {
  icon: React.ReactNode
  label: string
  active?: boolean
  onClick?: () => void
}

function NavItem({
  icon,
  label,
  active = false,
  onClick,
}: Readonly<NavItemProps>) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-3 w-full rounded-xl transition-all duration-200 group relative ${
        active
          ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-primary font-semibold shadow-sm border border-blue-100'
          : 'hover:bg-slate-100/80 text-muted-foreground hover:text-foreground border border-transparent hover:border-slate-200'
      }`}
    >
      <span
        className={`flex items-center justify-center h-8 w-8 rounded-lg ${
          active ? 'bg-primary/15 text-primary' : 'group-hover:bg-slate-200/50'
        }`}
      >
        {icon}
      </span>
      <span>{label}</span>
    </button>
  )
}
